<template>
  <div
    :class="[
      'activity-cv-block',
      { 'activity-cv-block--visible': isCertificatesVisible },
    ]"
  >
    <el-divider></el-divider>

    <div class="activity__subtitle">
      Certificates
    </div>

    <div class="activity-cv-block__visibility-toggler">
      <el-button type="primary" @click="toggleCertificatesVisibility">{{
        isCertificatesVisible ? 'Hide certificates' : 'Show certificates'
      }}</el-button>
    </div>

    <div class="activity-cv-block__content" v-if="isCertificatesVisible">
      <el-form
        class="activity-cv-block__item-form"
        label-position="top"
        v-for="(certificate, index) in certificateArray"
        :key="index"
        :rules="certificateFormRules"
        :model="certificate"
        ref="certificate"
      >
        <div class="row">
          <div class="col col-xs-12 col-md-6 col-lg-4">
            <el-form-item label="Type" prop="certificateTypeId">
              <el-select
                class="wide"
                v-model="certificate.certificateTypeId"
                placeholder="Choose from the list"
              >
                <el-option
                  v-for="type in certificateTypeDescriptions"
                  :key="type.value"
                  :label="type.description"
                  :value="type.value"
                >
                </el-option>
              </el-select>
            </el-form-item>
          </div>

          <div class="col col-xs-12 col-md-6 col-lg-4">
            <el-form-item label="Start day" prop="issueDate">
              <el-date-picker
                class="wide"
                v-model="certificate.issueDate"
                type="date"
                format="DD/MM/YYYY"
              >
              </el-date-picker>
            </el-form-item>
          </div>

          <div class="col col-xs-12 col-md-6 col-lg-4">
            <el-form-item label="Expiration date" prop="expirationDate">
              <el-date-picker
                class="wide"
                v-model="certificate.expirationDate"
                type="date"
                format="DD/MM/YYYY"
              >
              </el-date-picker>
            </el-form-item>
          </div>

          <div class="col col-xs-12">
            <el-form-item
              label="Description"
              prop="description"
              :error="certificate.storyError"
            >
              <el-input
                type="textarea"
                :rows="3"
                v-model="certificate.description"
              ></el-input>
            </el-form-item>
          </div>
        </div>

        <div class="activity-cv-block__buttons">
          <confirmation-dialog
            content="Are you sure to delete this certificate?"
            @on-confirm="deleteCertificate(certificate)"
          >
            <el-button
              class="activity-cv-block__remove-button"
              type="primary"
              :disabled="isButtonDisabled('remove-certificate')"
            >
            <el-icon><Delete/></el-icon>
            Delete
            </el-button>
          </confirmation-dialog>
          <el-button
            type="primary"
            @click="validateCertificate(certificate, index)"
            :disabled="isButtonDisabled('save-sertificate')"
          >
            Save certificate
          </el-button>
        </div>
      </el-form>

      <el-button type="primary" @click="addCertificate">
        <el-icon><Plus/></el-icon>
        Add certificate
      </el-button>
    </div>
  </div>
</template>

<script>
import {
  getCertificateTypeDescriptions,
  addCertificate,
  updateCertificate,
  removeCertificate,
} from '@/api/certificate.js';

import { showMessage } from '@/utils/messages.js';
import {
  parseErrorsByFields,
  isTwoObjectsEqual,
  removeArrayElementById,
  removeTimezoneOffset,
} from '@/utils/index.js';
import { disableButton } from '@/mixins/disableButton.js';

import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';

export default {
  name: 'EditCertificates',
  props: {
    certificatesData: {
      type: Array,
      required: false,
    },
    activityCVId: {
      type: Number,
      required: true,
    },
    activityId: {
      type: Number,
      required: true,
    },
  },
  mixins: [disableButton],
  components: {
    ConfirmationDialog,
  },
  data() {
    return {
      certificateArray: [],
      certificateTypeDescriptions: null,
      certificateFormRules: {
        certificateTypeId: [
          {
            required: true,
            message: 'Please choose certificate type',
            trigger: 'change',
          },
        ],
        description: [{ required: false, message: '', trigger: 'blur' }],
        issueDate: [
          {
            required: true,
            message: 'Please select the certificate start date',
            trigger: 'change',
          },
        ],
        expirationDate: [{ required: false, message: '', trigger: 'change' }],
      },
      isCertificatesVisible: false,
    };
  },
  methods: {
    showMessage,
    parseErrorsByFields,
    isTwoObjectsEqual,
    removeArrayElementById,
    removeTimezoneOffset,
    async fetchCertificateTypeDescriptions() {
      const { activityId } = this;
      const { success, data } = await getCertificateTypeDescriptions({
        activityId,
      });
      if (success) {
        this.certificateTypeDescriptions = data.items;
      }
    },
    async requestAddCertificate(certificate) {
      this.disableButton('save-certificate');
      const { activityCVId, removeTimezoneOffset } = this;
      const {
        certificateTypeId,
        description,
        issueDate,
        expirationDate,
      } = certificate;

      const { success, errors } = await addCertificate({
        activityCVId,
        certificateTypeId,
        description,
        issueDate: issueDate ? removeTimezoneOffset(issueDate) : null,
        expirationDate: expirationDate ? removeTimezoneOffset(issueDate) : null,
      });
      this.enableButton('save-certificate');

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Certificate has been added',
        });
        this.$emit('update-user-data');
      } else if (errors) {
        this.setCertificateErrors(
          certificate,
          this.parseErrorsByFields(errors),
        );
      }
    },
    async requestUpdateCertificate(certificate) {
      this.disableButton('save-certificate');
      const { removeTimezoneOffset } = this;
      const {
        certificateId,
        certificateTypeId,
        description,
        issueDate,
        expirationDate,
      } = certificate;

      const { success, errors } = await updateCertificate({
        certificateId,
        certificateTypeId,
        description,
        issueDate: issueDate ? removeTimezoneOffset(issueDate) : null,
        expirationDate: expirationDate
          ? removeTimezoneOffset(expirationDate)
          : null,
      });
      this.enableButton('save-certificate');

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Certificate has been updated',
        });
        this.$emit('update-user-data');
      } else if (errors) {
        this.setCertificateErrors(
          certificate,
          this.parseErrorsByFields(errors),
        );
      }
    },
    async requestRemoveCertificate(certificate) {
      this.disableButton('remove-certificate');
      const { certificateId } = certificate;
      const { success, errors } = await removeCertificate({ certificateId });
      this.enableButton('remove-certificate');

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Certificate has been removed',
        });
        this.$emit('update-user-data');
      } else if (errors) {
        this.setCertificateErrors(
          certificate,
          this.parseErrorsByFields(errors),
        );
      }
    },
    setCertificateErrors(certificate, errors) {
      const newErrors = {};
      for (const key in errors) {
        newErrors[key + 'Error'] = errors[key];
      }

      const certificateIndex = this.certificateArray.indexOf(certificate);
      this.certificateArray[certificateIndex] = {
        ...certificate,
        ...newErrors,
      };
    },
    setCertificatesData() {
      const { certificatesData } = this;

      this.certificateArray = certificatesData.map((certificate) => {
        return {
          certificateId: certificate.certificateId,
          certificateTypeId: certificate.certificateType
            ? certificate.certificateType.certificateTypeId
            : null,
          description: certificate.description,
          issueDate: certificate.issueDate,
          expirationDate: certificate.expirationDate,
        };
      });
    },
    addCertificate() {
      this.certificateArray.push({
        certificateId: null,
        certificateTypeId: null,
        description: null,
        issueDate: null,
        expirationDate: null,
      });
    },
    deleteCertificate(certificate) {
      const { certificateArray, removeArrayElementById } = this;

      const certificateIndex = certificateArray.indexOf(certificate);
      this.certificateArray = removeArrayElementById(
        certificateArray,
        certificateIndex,
      );

      if (certificate.certificateId) {
        this.requestRemoveCertificate(certificate);
      }
    },
    validateCertificate(certificate, index) {
      const {
        certificatesData,
        isCertificateFormFilled,
        removeTimezoneOffset,
        isTwoObjectsEqual,
      } = this;
      this.$refs.certificate[index].validate();

      if (isCertificateFormFilled(certificate)) {
        const { certificateId } = certificate;

        if (certificateId) {
          const addedCertificate = certificatesData.find(
            (certificateItem) =>
              certificateItem.certificateId === certificateId,
          );

          const oldData = {
            certificateId: addedCertificate.certificateId,
            certificateTypeId:
              addedCertificate.certificateType.certificateTypeId,
            description: addedCertificate.description,
            issueDate: addedCertificate.issueDate,
            expirationDate: addedCertificate.expirationDate,
          };

          const newData = {
            certificateId: certificate.certificateId,
            certificateTypeId: certificate.certificateTypeId,
            description: certificate.description,
            issueDate: certificate.issueDate,
            expirationDate: certificate.expirationDate,
          };

          if (isTwoObjectsEqual(newData, oldData)) {
            this.showMessage({
              type: 'error',
              message: 'There are no changes to be saved',
            });
          } else {
            this.requestUpdateCertificate(certificate);
          }
        } else {
          this.requestAddCertificate(certificate);
        }
      }
    },
    isCertificateFormFilled(certificate) {
      const requiredFields = {
        certificateTypeId: certificate.certificateTypeId,
        issueDate: certificate.issueDate,
      };

      return Object.values(requiredFields).every((value) => !!value);
    },
    toggleCertificatesVisibility() {
      this.isCertificatesVisible = !this.isCertificatesVisible;
    },
  },
  created() {
    const { certificatesData } = this;
    if (certificatesData) {
      this.setCertificatesData();
    }

    this.fetchCertificateTypeDescriptions();
    this.isCertificatesVisible = this.certificateArray.length === 0;
  },
  watch: {
    certificatesData(newVal) {
      if (newVal) {
        this.setCertificatesData();
      }
    },
  },
};
</script>
